import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { FiMail, FiTrash2, FiSend, FiX, FiFilter } from 'react-icons/fi';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/contact', { params: { status: filter || undefined } });
      setMessages(res.data.messages);
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (msg) => {
    setSelectedMsg(msg);
    setReplyText('');
    if (msg.status === 'unread') {
      try {
        await api.put(`/contact/${msg._id}/read`);
        fetchMessages();
      } catch (err) {}
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setReplying(true);
    try {
      await api.post(`/contact/${selectedMsg._id}/reply`, { reply: replyText });
      toast.success('Reply sent successfully!');
      setSelectedMsg(null);
      setReplyText('');
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success('Message deleted');
      setSelectedMsg(null);
      fetchMessages();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="admin-page-header">
        <h1>Messages</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '8px 16px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '0.88rem',
              outline: 'none'
            }}
          >
            <option value="">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <FiMail size={40} color="var(--text-muted)" />
          <p style={{ color: 'var(--text-muted)', marginTop: '16px' }}>No messages yet</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg) => (
              <tr key={msg._id} style={{ cursor: 'pointer' }} onClick={() => openMessage(msg)}>
                <td>
                  <span className={`status-badge status-${msg.status}`}>{msg.status}</span>
                </td>
                <td style={{ fontWeight: msg.status === 'unread' ? 700 : 400 }}>{msg.name}</td>
                <td>{msg.email}</td>
                <td>{msg.subject}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(msg.createdAt).toLocaleDateString()}
                </td>
                <td>
                  <button
                    className="admin-btn admin-btn-danger"
                    style={{ padding: '6px 10px' }}
                    onClick={(e) => { e.stopPropagation(); handleDelete(msg._id); }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Message Detail Modal */}
      {selectedMsg && (
        <div className="modal-overlay" onClick={() => setSelectedMsg(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2>{selectedMsg.subject}</h2>
              <button
                onClick={() => setSelectedMsg(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', padding: '4px', cursor: 'pointer' }}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="modal-meta">
              <span><strong>From:</strong> {selectedMsg.name}</span>
              <span><strong>Email:</strong> {selectedMsg.email}</span>
              <span><strong>Date:</strong> {new Date(selectedMsg.createdAt).toLocaleString()}</span>
              <span><strong>Status:</strong> <span className={`status-badge status-${selectedMsg.status}`}>{selectedMsg.status}</span></span>
            </div>

            <div className="modal-message">{selectedMsg.message}</div>

            {selectedMsg.status === 'replied' && selectedMsg.reply && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Your Reply:</h4>
                <div className="modal-message" style={{ borderLeft: '3px solid var(--accent-primary)' }}>
                  {selectedMsg.reply}
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Replied on {new Date(selectedMsg.repliedAt).toLocaleString()}
                </p>
              </div>
            )}

            {selectedMsg.status !== 'replied' && (
              <div className="modal-reply-form">
                <h4 style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Reply to {selectedMsg.name}:</h4>
                <textarea
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <div className="modal-actions">
                  <button className="admin-btn admin-btn-secondary" onClick={() => setSelectedMsg(null)}>
                    Cancel
                  </button>
                  <button className="admin-btn admin-btn-primary" onClick={handleReply} disabled={replying}>
                    {replying ? 'Sending...' : <><FiSend size={14} /> Send Reply</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
